import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Box, CircularProgress, Typography } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FieldWrapperVertical,
  SnackbarApiErrorMessage,
  TextArea,
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { IconLibrary } from '@product-framework/index';
import DialogCloseButton from './DialogCloseButton';

function SendMessageDialog({ open, onClose, jobId, serviceProviderId }) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, post } = useDataProvider(provider);

  const [loading, setLoading] = useState(false);

  const schema = yup.object().shape({
    message: yup.string().required(
      formatMessage({
        id: 'MESSAGE_IS_REQUIRED',
        defaultMessage: 'Message is required',
      }),
    ),
  });

  const methods = useForm({ resolver: yupResolver(schema) });

  const {
    handleSubmit,
    formState: { errors },
  } = methods;

  async function onFormSubmit(data) {
    setLoading(true);
    const body = {
      serviceProviderId,
      ...data,
    };
    const response = await post(
      `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/operations/send-message-to-service-provider`,
      body,
    );
    if (response.ok) {
      snackbar.showMessage(
        formatMessage({
          id: 'SEND_MESASGE_TO_SP_SUCCESS',
          defaultMessage: 'Sent message to service provider successfully',
        }),
      );
      onClose();
    } else {
      snackbar.showMessage({
        type: 'error',
        data: <SnackbarApiErrorMessage errors={response?.errors} />,
      });
    }
    setLoading(false);
  }

  const buttons = [
    {
      children: formatMessage({
        id: 'SEND_MESSAGE',
        defaultMessage: 'Send Message',
      }),
      type: 'submit',
      variant: 'contained',
      color: 'primary',
      startIcon: <IconLibrary.Yes />,
    },
    {
      children: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
      onClick: onClose,
      variant: 'contained',
      color: 'secondary',
      startIcon: <IconLibrary.Cancel />,
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.Message />
        {formatMessage({
          id: 'SEND_MESSAGE_HEADER',
          defaultMessage: 'Send Message',
        })}
        <DialogCloseButton disabled={loading} onClose={onClose} />
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogContent>
            {loading ? (
              <Box textAlign="center">
                <CircularProgress size="30px" />
                <Typography>Processing</Typography>
              </Box>
            ) : (
              <Grid2>
                <FieldWrapperVertical
                  field={{
                    name: 'message',
                    label: {
                      localeId: 'message',
                      defaultMessage: 'Message',
                    },
                  }}
                  errors={errors}>
                  <TextArea
                    sx={{ width: '100%' }}
                    rows={8}
                    {...methods.register('message')}
                    error={!!errors?.message?.message}
                    helperText={errors?.message?.message || ''}
                  />
                </FieldWrapperVertical>
              </Grid2>
            )}
          </DialogContent>
          <DialogActions
            display="flex"
            sx={{ justifyContent: 'center', my: 2 }}>
            {buttons.map(btn => {
              const { children, ...rest } = btn;
              return (
                <Button key={children} disabled={loading} {...rest}>
                  {children}
                </Button>
              );
            })}
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default SendMessageDialog;
